import React, { useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import _ from 'lodash';
import 'moment/locale/vi';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import ChargeHelper from 'features/Charge/chargeHelper';
import { useSelector } from 'react-redux';
import Utils from 'general/utils/Utils';
import KTTooltip from '../KTTooltip';
import MaintainerHelper, { MaintainerConstants } from 'features/Maintainer/maintainerHelper';
import AppRoute from 'general/constants/AppRoute';

moment.locale('vi');
const localizer = momentLocalizer(moment);

const KTCalendar = ({ tickets = [], selectedValue, startDate }) => {
  if (!tickets || !_.isArray(tickets) || !tickets.length) {
    return null;
  }

  const areaTree = useSelector((state) => state.app.areaTree);

  const getTicketAddress = (row) => {
    const area = ChargeHelper.Utils.getAreaById([areaTree], row?.chargeStation?.areaId);
    let parentArea;
    if (area?.typeCode === 'W') {
      parentArea = ChargeHelper.Utils.getAreaById([areaTree], area?.parentId);
    }
    return [row?.chargeStation?.address, area?.name, parentArea?.name]
      .filter(Boolean)
      .join(', ');
  };

  const getTicketColor = (type) => {
    const colors = {
      1: '#7E8299',
      2: '#F9B700',
      3: '#1877E7',
      4: '#EB4B4B',
      5: '#9B64F9',
    };
    return colors[type] || '#7E8299';
  };

  const getTicketBgColor = (type) => {
    const bgColors = {
      1: '#ECECF0',
      2: '#FFF7D9',
      3: '#DCEBFB',
      4: '#FBE4E5',
      5: '#F0E8FE',
    };
    return bgColors[type] || '#ECECF0';
  };

  const getBadgeStatusColor = (status) => {
    const colors = {
      [MaintainerConstants.TicketStatus.TODO]: '#7E8299',
      [MaintainerConstants.TicketStatus.CHECKIN]: '#3E97FF',
      [MaintainerConstants.TicketStatus.PENDING]: '#FFA800',
      [MaintainerConstants.TicketStatus.APPROVAL]: '#9B64F9',
      [MaintainerConstants.TicketStatus.DONE]: '#50CD89',
      [MaintainerConstants.TicketStatus.CANCEL]: '#E14E54',
    };
    return colors[status] || '#7E8299';
  };

  const getBadgeStatusBgColor = (status) => {
    const bgColors = {
      [MaintainerConstants.TicketStatus.TODO]: '#EEF1FA',
      [MaintainerConstants.TicketStatus.CHECKIN]: '#F2F5F9',
      [MaintainerConstants.TicketStatus.PENDING]: '#FFF8DD',
      [MaintainerConstants.TicketStatus.APPROVAL]: '#F0E8FF',
      [MaintainerConstants.TicketStatus.DONE]: '#E6FFDB',
      [MaintainerConstants.TicketStatus.CANCEL]: '#FFEEF3',
    };
    return bgColors[status] || '#EEF1FA';
  };

  const isDayView = ['HOM_QUA', 'HOM_NAY'].includes(selectedValue);
  const isWeekView = ['TUAN_TRUOC', 'TUAN_NAY'].includes(selectedValue);
  const isMonthView = selectedValue.includes('THANG');
  const isListView = !isDayView && !isWeekView && !isMonthView;

  const events = useMemo(() => {
    if (isMonthView) {
      const sortedTickets = [...tickets].sort((a, b) => {
        return new Date(a.deadline) - new Date(b.deadline);
      });

      return sortedTickets.map((t) => {
        const deadlineMoment = moment(t.deadline);
        const hourStart = deadlineMoment.clone().startOf('hour');
        const address = getTicketAddress(t);

        return {
          id: t.ticketId,
          title: address || 'Địa chỉ trạm sạc',
          start: hourStart.toDate(),
          end: hourStart.clone().add(1, 'hour').toDate(),
          resource: {
            time: Utils.formatDateTimeToString(t.deadline, 'HH:mm - DD/MM/YYYY'),
            address,
            color: getTicketColor(t.priority),
            bgColor: getTicketBgColor(t.priority),
            status: t.status,
            ticketId: t.ticketId,
          },
        };
      });
    } else {
      const grouped = _.groupBy(tickets, (t) =>
        moment(t.deadline).startOf('hour').toISOString()
      );

      return _.sortBy(
        _.map(grouped, (items, time) => {
          const start = new Date(time);
          return {
            id: time,
            start,
            end: moment(start).add(1, 'hour').toDate(),
            resource: {
              tickets: _.sortBy(items, 'deadline'),
            },
          };
        }),
        'start'
      );
    }
  }, [tickets, areaTree, isMonthView]);

  const TimeGridHeader = ({ date }) => (
    <div className="custom-time-header">
      <div className="day-name">{moment(date).format('dddd').toLowerCase()}</div>
      <div className={`day-number ${moment(date).isSame(moment(), 'day') ? 'active' : ''}`}>
        {moment(date).format('D')}
      </div>
    </div>
  );

  const MonthViewHeader = ({ label }) => (
    <div className="custom-month-header-label">{label}</div>
  );

  const MonthDateHeader = ({ label, date }) => {
    const isToday = moment(date).isSame(moment(), 'day');
    return (
      <div className="month-date-cell">
        {isToday && <span className="green-badge">Hôm nay</span>}
        <span className="date-label">{label}</span>
      </div>
    );
  };

  const CustomTimeSlotWrapper = ({ children }) => (
    <div style={{ height: '80px', overflow: 'hidden', position: 'relative' }}>
      {children}
    </div>
  );

  const CustomEvent = ({ event }) => {
    if (isMonthView) {
      const { time, address, color, bgColor, status, ticketId } = event.resource;

      const tooltipContent = `
        <div style="font-size: 13px; line-height: 1.6;">
          <div style="margin-bottom: 5px;">
            <strong>Trạng thái công việc:</strong>
            <span style="background:${getBadgeStatusBgColor(status)}; padding:4px 10px; border-radius:4px; margin-left:2px; color:${getBadgeStatusColor(status)}; font-weight:600;">
              ${MaintainerHelper.Utils.getTicketStatusName(status)}
            </span>
          </div>
          <div style="margin-bottom: 5px;">
            <strong>Thời gian cần xử lý:</strong>
            <strong style="margin-left:2px;">${time}</strong>
          </div>
          <div><span>${address}</span></div>
        </div>
      `;

      return (
        <KTTooltip text={tooltipContent} isHtmlContent>
          <div
            className="event-item-wrapper"
            style={{ borderLeftColor: color, backgroundColor: bgColor }}
            onClick={() => Utils.openInNewTab(`${AppRoute.myTicket}/${ticketId}`)}
          >
            <div className="event-title">{time}</div>
            <div className="event-address">{address}</div>
          </div>
        </KTTooltip>
      );
    } else {
      return (
        <div className="event-slot-scroll-container">
          {event.resource.tickets.map((t) => {
            const address = getTicketAddress(t);
            const color = getTicketColor(t.priority);
            const bgColor = getTicketBgColor(t.priority);
            const time = Utils.formatDateTimeToString(t.deadline, 'HH:mm - DD/MM/YYYY');

            const tooltipContent = `
              <div style="font-size: 13px; line-height: 1.6;">
                <div style="margin-bottom: 5px;">
                  <strong>Trạng thái:</strong>
                  <span style="background:${getBadgeStatusBgColor(t.status)}; padding:4px 10px; border-radius:4px; margin-left:2px; color:${getBadgeStatusColor(t.status)}; font-weight:600;">
                    ${MaintainerHelper.Utils.getTicketStatusName(t.status)}
                  </span>
                </div>
                <div style="margin-bottom: 5px;">
                  <strong>Thời gian:</strong> <strong>${time}</strong>
                </div>
                <div><span>${address}</span></div>
              </div>
            `;

            return (
              <KTTooltip key={t.ticketId} text={tooltipContent} isHtmlContent>
                <div
                  className="event-item-wrapper"
                  style={{ borderLeftColor: color, backgroundColor: bgColor, marginBottom: '4px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    Utils.openInNewTab(`${AppRoute.myTicket}/${t.ticketId}`);
                  }}
                >
                  <div className="event-title">{time}</div>
                  <div className="event-address">{address}</div>
                </div>
              </KTTooltip>
            );
          })}
        </div>
      );
    }
  };

  if (isListView) {
    const grouped = _.groupBy(tickets, (t) =>
      moment(t.deadline).format('dddd - DD/MM/YYYY')
    );

    const sortedGrouped = _.fromPairs(
      _.sortBy(_.toPairs(grouped), ([day]) => moment(day, 'dddd - DD/MM/YYYY').valueOf())
    );

    return (
      <div className="list-view-container">
        <style jsx>{`
          .list-view-container {
            padding: 0 20px 20px 20px;
            background: #fff;
            height: 100%;
            overflow-y: auto;
          }
          .list-header-date {
            font-weight: bold;
            color: #70757a;
            font-size: 14px;
            margin: 20px 0 10px;
            text-transform: capitalize;
            border-bottom: 1px solid #eee;
            padding-bottom: 8px;
          }
          .list-card {
            background: #f8f9fa;
            border-left: 4px solid;
            padding: 7px 10px;
            border-radius: 4px;
            box-sizing: border-box;
            margin-bottom: 10px;
          }
          .card-title {
            font-weight: bold;
            font-size: 12px;
            color: #494968;
            margin-bottom: 10px;
          }
          .card-address {
            font-size: 11px;
            color: #494968;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            margin-top: 7px;
          }
        `}</style>

        {_.map(sortedGrouped, (items, dateStr) => (
          <div key={dateStr}>
            <div className="list-header-date">{dateStr}</div>
            {items.map((t) => {
              const tooltipContent = `
                <div style="font-size: 13px; line-height: 1.6;">
                  <div style="margin-bottom: 5px;">
                    <strong>Trạng thái công việc:</strong>
                    <span style="background:${getBadgeStatusBgColor(t.status)}; padding:4px 10px; border-radius:4px; margin-left:2px; color:${getBadgeStatusColor(t.status)}; font-weight:600;">
                      ${MaintainerHelper.Utils.getTicketStatusName(t.status)}
                    </span>
                  </div>
                  <div style="margin-bottom: 5px;">
                    <strong>Thời gian cần xử lý:</strong>
                    <strong style="margin-left:2px;">${Utils.formatDateTimeToString(t.deadline, 'HH:mm - DD/MM/YYYY')}</strong>
                  </div>
                  <div><span>${getTicketAddress(t)}</span></div>
                </div>
              `;

              return (
                <KTTooltip key={t.ticketId} text={tooltipContent} isHtmlContent>
                  <div
                    className="list-card cursor-pointer"
                    style={{ borderLeftColor: getTicketColor(t.priority), backgroundColor: getTicketBgColor(t.priority) }}
                    onClick={() => Utils.openInNewTab(`${AppRoute.myTicket}/${t.ticketId}`)}
                  >
                    <div className="card-title">
                      {Utils.formatDateTimeToString(t.deadline, 'HH:mm - DD/MM/YYYY')}
                    </div>
                    <div className="card-address">{getTicketAddress(t)}</div>
                  </div>
                </KTTooltip>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`calendar-wrapper view-${isMonthView ? 'month' : isDayView ? 'day' : 'week'}`}>
      <style jsx global>{`
        .rbc-calendar {
          font-family: 'Inter', sans-serif;
          height: 85vh;
          background: #fff;
        }

        .rbc-time-gutter .rbc-timeslot-group {
          border: none !important;
          height: 80px !important;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-left: 10px;
          padding-right: 5px;
        }

        .rbc-label {
          font-size: 13px;
          color: #494968;
          font-weight: 600;
        }

        .view-day .rbc-header,
        .view-week .rbc-header {
          padding: 5px 0 !important;
          border-bottom: none !important;
          border-left: none !important;
        }

        .custom-time-header {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .day-name {
          font-size: 12px;
          color: #9ca3af;
          text-transform: capitalize;
        }

        .day-number {
          font-size: 16px;
          font-weight: 600;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .day-number.active {
          background: #10b981;
          color: #fff;
          border-radius: 50%;
        }

        .view-month .rbc-header {
          padding: 15px 0 !important;
          border-bottom: 1px solid #eee !important;
        }

        .custom-month-header-label {
          font-size: 13px;
          color: #9ca3af;
          font-weight: 500;
          text-transform: capitalize;
        }

        .month-date-cell {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 4px 10px;
        }

        .green-badge {
          background: #10b981;
          color: white;
          border-radius: 4px;
          padding: 0 5px;
          font-size: 11px;
          margin-right: auto;
        }

        .rbc-time-header-content .rbc-allday-cell {
          display: none !important;
        }

        .rbc-event {
          background: none !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
          width: 100% !important;
          height: auto !important;
          overflow: visible !important;
        }

        .rbc-events-container {
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          padding: 2px 0 !important;
          height: 100% !important;
          width: 100% !important;
        }

        .rbc-event-content {
          width: 100% !important;
          height: auto !important;
          position: relative !important;
          padding: 2px;
        }

        .event-item-wrapper {
          background: #f8f9fa;
          border-left: 4px solid;
          padding: 7px 10px;
          border-radius: 4px;
          box-sizing: border-box;
          width: 100%;
          overflow: hidden;
        }

        .event-title {
          font-weight: bold;
          font-size: 12px;
          color: #494968;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .event-address {
          font-size: 11px;
          color: #494968;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-top: 7px;
        }

        .rbc-event-label {
          display: none !important;
        }

        .rbc-event:focus,
        .rbc-day-slot .rbc-background-event:focus {
          outline: none;
        }

        .rbc-timeslot-group {
          min-height: 80px !important;
          height: 80px !important;
        }

        .event-slot-scroll-container {
          max-height: 78px;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .event-slot-scroll-container::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <Calendar
        localizer={localizer}
        events={events}
        view={isDayView ? 'day' : isWeekView ? 'week' : 'month'}
        date={moment(startDate).toDate()}
        toolbar={false}
        allDayAccessor={() => false}
        dayLayoutAlgorithm="no-overlap"
        min={new Date(0, 0, 0, 0, 0, 0)}
        max={new Date(0, 0, 0, 23, 59, 59)}
        step={60}
        showAllEvents={true}
        timeslots={1}
        components={{
          event: CustomEvent,
          header: TimeGridHeader,
          timeSlotWrapper: CustomTimeSlotWrapper,
          month: {
            header: MonthViewHeader,
            dateHeader: MonthDateHeader,
          },
        }}
        formats={{
          timeGutterFormat: 'HH:mm',
          weekdayFormat: (date) => moment(date).format('dddd'),
        }}
      />
    </div>
  );
};

export default KTCalendar;